import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Box,
  Typography,
  Alert,
  AlertTitle,
  CircularProgress,
  Paper,
  MenuItem,
  Select,
  InputLabel,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { useCurrentUser } from '../../hooks/useApi';
import { UploadFile as UploadFileIcon } from '@mui/icons-material';

interface TemplateUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (templateId: string) => void;
}

interface UploadResponse {
  success: boolean;
  templateId?: string;
  message?: string;
  warnings?: string[];
  errors?: string[];
  controls?: string[];
}

export const TemplateUploadDialog: React.FC<TemplateUploadDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { data: user } = useCurrentUser();
  const queryClient = useQueryClient();

  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<'personal' | 'global'>('personal');
  const [languageCode, setLanguageCode] = useState('en-US');
  const [uploadResult, setUploadResult] = useState<UploadResponse | null>(null);

  const hasAdminPermission = user?.permissions?.includes('deliverables.all');

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('No file selected');

      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', name);
      formData.append('description', description);
      formData.append('type', type);
      formData.append('languageCode', languageCode);

      const response = await authenticatedFetch('/api/deliverables/templates/upload', {
        method: 'POST',
        body: formData,
        timeout: 60000, // 60 second timeout for file upload
      });

      if (!response?.ok) {
        const errorData = await response?.json();
        throw new Error(errorData?.error || 'Upload failed');
      }

      return await response.json();
    },
    onSuccess: (data: UploadResponse) => {
      setUploadResult(data);
      // Invalidate templates query to refresh list
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: (error: Error) => {
      console.error('Upload error:', error);
    },
  });

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const droppedFile = acceptedFiles[0];
        setFile(droppedFile);
        // Auto-populate name from filename if empty
        if (!name) {
          setName(droppedFile.name.replace('.docx', ''));
        }
      }
    },
    accept: {
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    maxFiles: 1,
    maxSize: 50 * 1024 * 1024, // 50MB
    disabled: uploadMutation.isPending,
  });

  const handleClose = () => {
    if (!uploadMutation.isPending) {
      setFile(null);
      setName('');
      setDescription('');
      setType('personal');
      setLanguageCode('en-US');
      setUploadResult(null);
      onClose();
    }
  };

  const handleUpload = () => {
    uploadMutation.mutate();
  };

  const canUpload =
    file !== null &&
    name.trim().length > 0 &&
    (type === 'personal' || hasAdminPermission);

  // Success view after upload
  if (uploadResult && uploadResult.success) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Upload Successful</DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            <AlertTitle>Template uploaded successfully!</AlertTitle>
            {uploadResult.message}
          </Alert>

          {uploadResult.warnings && uploadResult.warnings.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>Warnings</AlertTitle>
              {uploadResult.warnings.map((warning, index) => (
                <Typography key={index} variant="body2">
                  • {warning}
                </Typography>
              ))}
            </Alert>
          )}

          {uploadResult.controls && uploadResult.controls.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom>
                Detected Controls ({uploadResult.controls.length}):
              </Typography>
              <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                {uploadResult.controls.map((control, index) => (
                  <Typography key={index} variant="body2">
                    • {control}
                  </Typography>
                ))}
              </Box>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (uploadResult.templateId) {
                onSuccess(uploadResult.templateId);
                handleClose();
              }
            }}
          >
            View Details
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Upload Template</DialogTitle>
      <DialogContent>
        {/* Dropzone */}
        <Box
          {...getRootProps()}
          sx={{
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'grey.400',
            borderRadius: 2,
            p: 4,
            mb: 3,
            textAlign: 'center',
            bgcolor: isDragActive ? 'action.hover' : 'background.paper',
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'action.hover',
            },
          }}
        >
          <input {...getInputProps()} />
          <UploadFileIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body1" gutterBottom>
            {isDragActive ? 'Drop the file here' : 'Drag & drop your .docx file here'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            or click to browse
          </Typography>
          {file && (
            <Paper variant="outlined" sx={{ mt: 2, p: 1, bgcolor: 'success.50' }}>
              <Typography variant="body2">
                ✓ Selected: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
              </Typography>
            </Paper>
          )}
        </Box>

        {/* Template Name */}
        <TextField
          fullWidth
          label="Template Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          disabled={uploadMutation.isPending}
          helperText={`${name.length}/255 characters`}
          sx={{ mb: 2 }}
        />

        {/* Description */}
        <TextField
          fullWidth
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          rows={3}
          disabled={uploadMutation.isPending}
          sx={{ mb: 2 }}
        />

        {/* Template Type */}
        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <FormLabel component="legend">Template Type *</FormLabel>
          <RadioGroup
            row
            value={type}
            onChange={(e) => setType(e.target.value as 'personal' | 'global')}
          >
            <FormControlLabel
              value="personal"
              control={<Radio />}
              label="Personal"
              disabled={uploadMutation.isPending}
            />
            <FormControlLabel
              value="global"
              control={<Radio />}
              label="Global"
              disabled={!hasAdminPermission || uploadMutation.isPending}
            />
          </RadioGroup>
          {!hasAdminPermission && (
            <Typography variant="caption" color="text.secondary">
              Global templates require deliverables.all permission
            </Typography>
          )}
        </FormControl>

        {/* Language Code */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Language Code</InputLabel>
          <Select
            value={languageCode}
            label="Language Code"
            onChange={(e) => setLanguageCode(e.target.value)}
            disabled={uploadMutation.isPending}
          >
            <MenuItem value="en-US">English (US)</MenuItem>
            <MenuItem value="en-GB">English (UK)</MenuItem>
            <MenuItem value="es-ES">Spanish</MenuItem>
            <MenuItem value="fr-FR">French</MenuItem>
            <MenuItem value="de-DE">German</MenuItem>
            <MenuItem value="it-IT">Italian</MenuItem>
            <MenuItem value="pt-BR">Portuguese (Brazil)</MenuItem>
            <MenuItem value="zh-CN">Chinese (Simplified)</MenuItem>
            <MenuItem value="ja-JP">Japanese</MenuItem>
          </Select>
        </FormControl>

        {/* Error Display */}
        {uploadMutation.isError && (
          <Alert severity="error">
            <AlertTitle>Upload Failed</AlertTitle>
            {uploadMutation.error instanceof Error
              ? uploadMutation.error.message
              : 'An error occurred during upload'}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploadMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!canUpload || uploadMutation.isPending}
          startIcon={uploadMutation.isPending ? <CircularProgress size={20} /> : null}
        >
          {uploadMutation.isPending ? 'Uploading...' : 'Upload Template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
